import { useMemo, useState, useEffect, useRef } from "react";
import { SignalEvidencePanel } from "@/components/SignalEvidencePanel";
import { CaChartPanel } from "@/components/CaChartPanel";
import { LiquidityPoolToolbar } from "@/components/LiquidityPoolToolbar";
import { TokenSocialsEmbed } from "@/components/TokenSocialsEmbed";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useApp } from "@/context/AppCtx";
import { useStrategist } from "@/hooks/useStrategist";
import { useIsMobile } from "@/hooks/useIsMobile";
import { X, GripVertical, Target, Activity, Zap, Calendar } from "lucide-react";
import { useSocket } from "@/context/SocketContext";
import { TraderCalendarModal } from "@/components/TraderCalendarModal";
// import { MobileAgentControls } from "@/components/MobileAgentControls";

export function DashboardViewport() {
  const { caMintInput, setCaMintInput, autoScrollEnabled, setAutoScrollEnabled: _setAutoScrollEnabled, setIsFocusChartVisible, solPrice } = useApp() as any;
  const { recommendations, snapshot } = useStrategist();
  const isMobile = useIsMobile();
  const { serverOpenPositions, socket, serverPaperStats } = useSocket();

  // Trader's Calendar Modal state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [paperTrades, setPaperTrades] = useState<any[]>([]);

  useEffect(() => {
    fetch("/api/server-paper-trades")
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setPaperTrades(data);
        } else if (data && Array.isArray(data.trades)) {
          setPaperTrades(data.trades);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!socket) return;
    const newTradeHandler = (t: any) => {
      setPaperTrades(prev => [t, ...prev].sort((a, b) => b.timestamp - a.timestamp));
    };
    const historicalHandler = (payload: any) => {
      if (Array.isArray(payload)) {
        setPaperTrades(payload);
      } else if (payload && Array.isArray(payload.trades)) {
        setPaperTrades(payload.trades);
      }
    };
    const streamChunkHandler = (chunkPayload: any) => {
      if (chunkPayload && Array.isArray(chunkPayload.trades)) {
        setPaperTrades(prev => {
          const existingIds = new Set(prev.map(t => t.id || `${t.mint}_${t.timestamp}`));
          const incoming = chunkPayload.trades.filter((t: any) => !existingIds.has(t.id || `${t.mint}_${t.timestamp}`));
          if (incoming.length === 0) return prev;
          return [...prev, ...incoming].sort((a, b) => b.timestamp - a.timestamp);
        });
      }
    };

    socket.on("server-paper-new-trade", newTradeHandler);
    socket.on("server-paper-historical-trades", historicalHandler);
    socket.on("server-paper-trades-stream-chunk", streamChunkHandler);
    return () => {
      socket.off("server-paper-new-trade", newTradeHandler);
      socket.off("server-paper-historical-trades", historicalHandler);
      socket.off("server-paper-trades-stream-chunk", streamChunkHandler);
    };
  }, [socket]);

  // Auto-mode: continuously tracks the position with the highest PnL or moves instantly to next top token
  const [autoMode, setAutoMode] = useState(false);

  useEffect(() => {
    if (!autoMode) return;
    const pick = () => {
      const validOpen = (serverOpenPositions || []).filter((p: any) => 
        p && p.mint && !p.isRugged && !p.isDead && p.mint.toLowerCase() !== "dmxgg48pyynprxdbkxvtzs1kzojc6g6efme8jaoypump" && (p.pnlPct == null || p.pnlPct > -0.75)
      );
      if (validOpen.length > 0) {
        const best = [...validOpen].sort((a, b) => (b.pnlPct ?? 0) - (a.pnlPct ?? 0))[0];
        if (best?.mint && best.mint !== caMintInput) setCaMintInput(best.mint);
      } else {
        const top = recommendations.find((r: any) => !r.isDead && !r.isRugged && r.mint && r.mint.toLowerCase() !== "dmxgg48pyynprxdbkxvtzs1kzojc6g6efme8jaoypump");
        if (top?.mint && top.mint !== caMintInput) setCaMintInput(top.mint);
      }
    };
    pick(); // run immediately
    const id = setInterval(pick, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMode, serverOpenPositions, recommendations, caMintInput]);
  
  const [trackedMints, setTrackedMints] = useState<string[]>([]);
  const [dismissedMints, setDismissedMints] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const activeRecMints = useMemo(() => new Set(recommendations.map(r => r.mint)), [recommendations]);

  // Detect manual scroll to pause auto-follow
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      // If user scrolls up, disable auto-scroll
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 100;
      if (!isAtBottom && autoScrollEnabled) {
         // Optionally we could auto-disable here, but user asked for a button
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [autoScrollEnabled]);

  // Custom height management
  const [focusHeight, setFocusHeight] = useState(isMobile ? 700 : 1000);
  const [_matrixRowHeight, _setMatrixRowHeight] = useState(600);

  // Auto-Cleanup Engine
  useEffect(() => {
    const INACTIVITY_LIMIT_MS = 5 * 60 * 1000;
    const now = Date.now();
    
    setTrackedMints(prev => {
      const next = prev.filter(mint => {
        if (activeRecMints.has(mint)) return true;
        if (mint === caMintInput) return true;

        const allTokens = [...(snapshot.visibleTokens || [])];
        const tokenData = allTokens.find(t => t.mint === mint);
        
        const lastTrade = tokenData 
          ? ('lastTradeMs' in tokenData ? tokenData.lastTradeMs : (tokenData as any).lastUpdatedMs) 
          : 0;
        const isDead = lastTrade > 0 && (now - lastTrade > INACTIVITY_LIMIT_MS);
        
        if (isDead) {
          setDismissedMints(d => new Set(d).add(mint));
          return false;
        }
        return true;
      });
      return next.length !== prev.length ? next : prev;
    });
  }, [activeRecMints, snapshot, caMintInput]);

  useEffect(() => {
    if (recommendations.length === 0) return;
    setTrackedMints(prev => {
      const next = [...prev];
      let changed = false;
      recommendations.forEach(rec => {
        if (!next.includes(rec.mint) && rec.mint !== caMintInput && !dismissedMints.has(rec.mint) && next.length < 20) {
          next.push(rec.mint);
          changed = true;
        }
      });
      
      if (changed && autoScrollEnabled) {
         setTimeout(() => {
           scrollContainerRef.current?.scrollTo({
             top: scrollContainerRef.current.scrollHeight,
             behavior: "smooth"
           });
         }, 100);
      }

      return changed ? next : prev;
    });
  }, [recommendations, caMintInput, dismissedMints, autoScrollEnabled]);

  const focusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!caMintInput) {
      setIsFocusChartVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFocusChartVisible(entry.isIntersecting);
      },
      { threshold: 0.1, root: scrollContainerRef.current }
    );
    if (focusRef.current) observer.observe(focusRef.current);
    return () => observer.disconnect();
  }, [caMintInput, setIsFocusChartVisible]);

  // Auto-scroll to top when a new token is opened
  useEffect(() => {
    if (caMintInput && scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [caMintInput]);


  return (
    <div className="flex h-full flex-col bg-[var(--color-bg-editor)]">
      <div className="relative flex flex-1 overflow-hidden">
        <div className="flex h-full w-full flex-col relative">
                
                {/* GLOBAL GRIP STRIP (Left Edge) — desktop only */}
                {!isMobile && (
                  <div className="absolute left-0 top-0 bottom-0 w-2 flex items-center justify-center bg-transparent cursor-grab active:cursor-grabbing hover:bg-white/5 transition-colors z-[100] group">
                     <GripVertical className="size-3 opacity-0 group-hover:opacity-100 text-emerald-500/50 transition-all" />
                  </div>
                )}

                {/* UNIFIED SCROLLABLE CONTENT */}
                <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto custom-scrollbar relative ${isMobile ? "bg-transparent px-2 pb-[100px] pt-14" : "pl-6 pr-4 bg-transparent"} space-y-8 pb-40`}>
                   
                   {/* Main Focus Area */}
                   {caMintInput && (
                     <div ref={focusRef} className="flex flex-col gap-2 pt-2">
                        <div className="relative w-full rounded-xl overflow-hidden border border-white/5 shadow-2xl bg-black/40" style={{ height: focusHeight }}>
                           <div className="absolute top-2 right-2 z-[30] flex items-center gap-1.5">
                              {activeRecMints.has(caMintInput) && (
                                <div className="flex items-center gap-1 bg-emerald-500/90 text-white text-[8px] font-black uppercase px-1.5 py-0.5 rounded shadow-lg border border-white/20 animate-pulse">
                                  <Activity className="size-2" />
                                  <span>Active</span>
                                </div>
                              )}
                              <span className="bg-white/10 backdrop-blur-md text-white text-[8px] font-black uppercase px-1.5 py-0.5 rounded shadow-lg border border-white/10">Focus</span>
                                {/* Auto-mode toggle — tracks the position with highest PnL */}
                                <button
                                  onClick={() => setAutoMode(p => !p)}
                                  title={autoMode ? "Auto-mode ON: tracking highest PnL position" : "Auto-mode OFF: click to auto-follow best position"}
                                  className={`flex items-center gap-1 text-[8px] font-black uppercase px-1.5 py-0.5 rounded border transition-all ${
                                    autoMode
                                      ? "bg-amber-500/20 text-amber-400 border-amber-500/40 shadow-[0_0_8px_rgba(245,158,11,0.3)] animate-pulse"
                                      : "bg-white/5 text-white/40 border-white/10 hover:bg-white/10 hover:text-white/70"
                                  }`}
                                >
                                  <Zap className="size-2.5" fill={autoMode ? "currentColor" : "none"} />
                                  Auto
                                </button>
                                {/* Calendar Journal button */}
                                <button
                                  onClick={() => setIsCalendarOpen(true)}
                                  title="FlyBrain Trader's Calendar & KPIs"
                                  className="flex items-center gap-1 text-[8px] font-black uppercase px-1.5 py-0.5 rounded border bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:border-emerald-500/50 transition-all shadow-sm"
                                >
                                  <Calendar className="size-2.5" />
                                  Journal
                                </button>
                                <button onClick={() => setCaMintInput("")} className="p-1 bg-black/60 hover:bg-rose-500 text-white rounded-lg transition-all shadow-xl">
                                  <X className="size-3" />
                                </button>
                             </div>
                             <ErrorBoundary>
                                <CaChartPanel mint={caMintInput} isFocus={true} />
                             </ErrorBoundary>
                          </div>
                          {/* Calendar Button under the graph */}
                          <div className="flex items-center justify-between px-1">
                             <button
                               type="button"
                               onClick={() => setIsCalendarOpen(true)}
                               className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 hover:border-emerald-500/50 text-emerald-400 text-xs font-mono font-bold transition-all shadow-sm group"
                             >
                               <Calendar className="size-3.5 group-hover:scale-110 transition-transform" />
                               <span>Trader's PnL Calendar & Performance</span>
                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-normal">
                                  {serverPaperStats?.totalCount || paperTrades.length} Trades
                                </span>
                             </button>
                          </div>
                          {/* <ErrorBoundary> */}
                             <LiquidityPoolToolbar
                              mint={caMintInput}
                              solPrice={solPrice}
                           />
                        {/* </ErrorBoundary> */}
                        {/* Socials embed — mobile only, shown below LP toolbar */}
                        {isMobile && (
                          <ErrorBoundary>
                            <TokenSocialsEmbed mint={caMintInput} />
                          </ErrorBoundary>
                        )}
                        {!isMobile && (
                          <div
                             className="h-1.5 w-full bg-white/5 hover:bg-emerald-500/20 cursor-row-resize rounded-full transition-colors flex items-center justify-center group"
                             onMouseDown={(e) => {
                                const startY = e.clientY;
                                const startH = focusHeight;
                                const onMove = (me: MouseEvent) => setFocusHeight(Math.max(300, startH + (me.clientY - startY)));
                                const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                                window.addEventListener("mousemove", onMove);
                                window.addEventListener("mouseup", onUp);
                             }}
                           >
                             <div className="w-32 h-0.5 bg-white/10 rounded-full group-hover:bg-emerald-500/40" />
                          </div>
                        )}
                        <ErrorBoundary>
                           <SignalEvidencePanel variant={isMobile ? "compact" : "wide"} />
                        </ErrorBoundary>
                        {/* {isMobile && (
                          <ErrorBoundary>
                            <MobileAgentControls />
                          </ErrorBoundary>
                        )} */}
                     </div>
                   )}

                   {/* Matrix Area */}
                   {/* <div className="flex flex-col gap-6">
                       {trackedMints.length > 0 && (
                        <div className="flex items-center justify-between px-2 py-1.5 bg-black/20 border-y border-white/5 rounded-lg mb-4">
                           <div className="flex items-center gap-2">
                              <div className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              <span className="text-[10px] font-black uppercase tracking-widest text-[var(--color-fg-dim)]">Signal Matrix ({trackedMints.length}/20)</span>
                           </div>
                           <div className="flex items-center gap-4">
                              <button 
                                onClick={() => setAutoScrollEnabled(!autoScrollEnabled)}
                                className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-tight transition-all px-2 py-1 rounded border ${autoScrollEnabled ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" : "text-[var(--color-fg-dim)]/40 hover:text-[var(--color-fg-dim)] border-transparent"}`}
                              >
                                 <Activity className={`size-3 ${autoScrollEnabled ? "animate-pulse" : ""}`} />
                                 <span>Follow: {autoScrollEnabled ? "ON" : "OFF"}</span>
                              </button>
                              <button 
                                onClick={() => { setDismissedMints(new Set([...Array.from(dismissedMints), ...trackedMints])); setTrackedMints([]); }}
                                className="text-[9px] font-bold uppercase tracking-tight text-[var(--color-fg-dim)]/40 hover:text-rose-400 transition-colors"
                              >
                                Clear All
                              </button>
                           </div>
                        </div>
                      )}

                      <div className={`grid gap-6 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
                         {trackedMints.map(mint => (
                           <div key={mint} className="flex flex-col gap-2">
                              <div className="relative w-full rounded-xl overflow-hidden border border-white/5 bg-black/40 group/chart shadow-xl" style={{ height: matrixRowHeight }}>
                                 <div className="absolute top-2 left-2 z-[30]">
                                    {activeRecMints.has(mint) ? (
                                       <div className="flex items-center gap-1 bg-emerald-500 text-white text-[8px] font-black uppercase px-1.5 py-0.5 rounded shadow-lg animate-pulse">
                                         <Activity className="size-2" />
                                         <span>Live</span>
                                       </div>
                                    ) : (
                                       <div className="bg-white/5 backdrop-blur-md text-[var(--color-fg-dim)] text-[8px] font-black uppercase px-1.5 py-0.5 rounded border border-white/5">
                                         Inactive
                                       </div>
                                    )}
                                 </div>
                                 <div className="absolute top-2 right-2 z-[30] flex items-center gap-1.5 opacity-0 group-hover/chart:opacity-100 transition-opacity">
                                    <button onClick={() => openTokenInChart(mint)} className="p-1.5 bg-black/60 hover:bg-emerald-500 text-white rounded-lg shadow-xl"><Maximize2 className="size-3" /></button>
                                    <button onClick={() => dismissMint(mint)} className="p-1.5 bg-black/60 hover:bg-rose-500 text-white rounded-lg shadow-xl"><X className="size-3" /></button>
                                 </div>
                                 <ErrorBoundary>
                                    <LazyCaChartPanel mint={mint} onTitleClick={() => openTokenInChart(mint)} />
                                 </ErrorBoundary>
                              </div>
                              <div className="px-1">
                                 <ErrorBoundary>
                                    <SignalEvidencePanel mint={mint} />
                                 </ErrorBoundary>
                              </div>
                           </div>
                         ))}
                      </div>

                      {trackedMints.length > 0 && (
                        <div 
                           className="h-1.5 w-full bg-white/5 hover:bg-[#2EA8FF]/20 cursor-row-resize rounded-full transition-colors flex items-center justify-center group mt-4"
                           onMouseDown={(e) => {
                              const startY = e.clientY;
                              const startH = matrixRowHeight;
                              const onMove = (me: MouseEvent) => setMatrixRowHeight(Math.max(300, startH + (me.clientY - startY)));
                              const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                              window.addEventListener("mousemove", onMove);
                              window.addEventListener("mouseup", onUp);
                           }}
                         >
                           <div className="w-48 h-0.5 bg-white/10 rounded-full group-hover:bg-[#2EA8FF]/40" />
                        </div>
                      )}
                   </div> */}

                   {!caMintInput && trackedMints.length === 0 && (
                      <div className="flex flex-col items-center justify-center h-full text-center opacity-10 py-40">
                         <div className="size-20 rounded-[2.5rem] border-2 border-dashed border-white/20 flex items-center justify-center mb-6">
                            <Target className="size-10" />
                         </div>
                         <p className="text-[12px] font-black uppercase tracking-[0.3em]">Awaiting Target Lock</p>
                      </div>
                   )}
                </div>
        </div>
      </div>

      {/* Trader Calendar Modal */}
      <TraderCalendarModal
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        trades={paperTrades}
        solPrice={solPrice}
        onSelectMint={setCaMintInput}
        backendStats={serverPaperStats}
      />
    </div>
  );
}
